<template>
  <div>
    <popup-hint hint="formulas">
      <template v-slot:breadcrumbs>
        <breadcrumbs :back="[{text:'Blending',to:{name:'ProductionBlending'}}]" home="Formulas"/>
      </template>
      <template v-slot:top>
        Formulas specify the inputs required to produce a specific output in blending.
      </template>
      <template v-slot:bottom>
        Formulas are used for convenience and consistency.
      </template>
      <template v-slot:actions="{close}">
        <v-btn @click="close()" :to="{name:'ProductionBlendingFormulasEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <purtrak-table name="formulas" objectType="formulas" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="formulas2" v-model="selected"
                   fields="id,name,active,outputName,approvedFormulation,excise,duration"
                   :listFilters="listFilters"
                   :item-class="formula => (formula.active ? '' : 'grey--text')"
                   @dblclick="formula => $router.push({name:'ProductionBlending',query:{formulaId:formula.id}})"
                   @itemExpanded="({item, value}) => expandFormula(item, value)"
                   title="Formulas"
                   :print-info="{pageSize:'portrait'}"
                   download-endpoint="formulasExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.status='1';filters.search='';filters.excise=''">
          <v-list-item>
            <filter-search label="Name" v-model="filters.search"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Produces" dense outlined :items="exciseTypes" v-model="filters.excise"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">Processes: {{filters.search}}</v-chip>
          <v-chip v-show="filters.excise!=''" close small class="ml-1" @click:close="filters.excise=''">{{exciseTypes.find(e => e.value === filters.excise).text}}</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu v-if="!readOnly">
        <v-list-item :to="{name:'ProductionBlendingFormulasEditNew'}"><v-list-item-title>Create formula</v-list-item-title></v-list-item>
      </template>

      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'ProductionBlendingFormulasEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'ProductionBlendingFormulasEdit',params:{formulaId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.name}}</a>
          </v-hover>
          <template v-else>{{item.name}}</template>
          <div v-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.excise="{item}">
        {{exciseTypes.find(e => e.value === item.excise).text}} {{item.excise==='UserSpiritsReductionFormulationOther'?`(${item.approvedFormulation})`:''}}
      </template>
      <template v-slot:item.duration="{value}">{{value | duration}}</template>
      <template v-slot:item.create="{item}">
        <v-btn v-if="item.active" rounded color="success" small :to="{name:'ProductionBlendingEditNew',params:{formulaId:item.id}}">Blend</v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined class="ml-4">Formula operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ProductionBlending',query:{formulaId:item.id}}"><v-list-item-title>Blending history</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Blending',to:{name:'ProductionBlending'}},{text:'Formulas',to:{name:'ProductionBlendingFormulas'}}]},query:{to:'reports/production/ReportsBlendingActivity',formulaId:item.id}}"><v-list-item-title>Blending activity report</v-list-item-title></v-list-item>
            <v-list-item v-if="company.allowAudit && company.permissions.includes('audit')" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item v-if="item.active" exact :to="{name:'ProductionBlendingEditNew',params:{formulaId:item.id}}"><v-list-item-title>Blend</v-list-item-title></v-list-item>
              <v-list-item @click="printBlend(item.id,{},true)"><v-list-item-title>Print</v-list-item-title></v-list-item>
              <v-divider/>
              <v-list-item exact :to="{name:'ProductionBlendingFormulasEdit',params:{formulaId:item.id}}"><v-list-item-title>Edit formula</v-list-item-title></v-list-item>
              <v-list-item exact :to="{name:'ProductionBlendingFormulasEdit',params:{formulaId:item.id,copy:true}}"><v-list-item-title>Copy formula</v-list-item-title></v-list-item>
              <v-list-item @click="formula=item;showDelete=true"><v-list-item-title>Delete formula</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length" >
          <v-card class="my-4" :class="expansionPanelClass">
            <v-simple-table dense class="pl-4 text-caption" :class="expansionPanelClass" style="width:50%">
              <thead>
                <tr>
                  <th :class="expansionPanelClass" width="60%">Input</th>
                  <th class="text-right" :class="expansionPanelClass" width="40%">Quantity</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="input of item.inputs" :key="input.id">
                  <td class="text-caption">{{input.itemName}}</td>
                  <td class="text-caption text-right">
                    <template v-if="input.type!=='Item'">{{input.quantity |number(1)}}% {{ input.itemUnits }}</template>
                    <template v-else>{{input.quantity | number(input.precision)}} {{input.itemUnits}}</template>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="formula.name" @delete="performDeleteFormula"/>
  </div>
</template>

<script>
import productionBlendingPrintBlend from '@/views/production/blending/productionBlendingPrintBlend'

export default {
  name: 'ProductionBlendingFormulas',
  mixins: [productionBlendingPrintBlend],
  data() {
    return {
      filters: {
        status: '1',
        excise: '',
        search: ''
      },
      preferences: {},
      formula: {},
      showDelete: false,
      exciseTypes: [{value:'',text:'All'},{divider:true},
                    {text:'Spirit',value:'SpiritsReductionBulkSpiritsReturnedToSpiritProduction'},
                    {text:'Wine',value:'WineReductionNonDutyPaid'},
                    {text:'Approved formulation',value:'UserSpiritsReductionFormulationOther'},{divider:true},
                    {text:'Denatured alcohol - DA-2F',value:'SpiritsReductionDenaturedSpiritsFuelDA2F'},
                    {text:'Denatured alcohol - DA-2G',value:'SpiritsReductionDenaturedSpiritsFuelDA2G'},
                    {text:'Denatured alcohol - Other grades',value:'SpiritsReductionDenaturedSpiritsOther'},
                    {text:'Specially denatured alcohol',value:'SpiritsReductionSpeciallyDenaturedSpirits'}
                    ],
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'outputName', visible:true},
                       { value: 'excise', visible:true},
                       { value: 'duration', visible:true},
                       { value: 'create', visible:true}],
      headers: [{ text: 'FORMULA', value: 'name', width:'30%',sortable:true},
                { text: 'OUTPUT', value: 'outputName', width:'30%',sortable:true},
                { text: 'PRODUCES', value: 'excise', width:'30%',sortable:false},
                { text: 'AVG DURATION', value: 'duration', width:'20%',sortable:true,omit:() => !this.preferences.blendDuration},
                { text: 'CREATE', value: 'create', width:'15%',sortable:false,align:'center',print:false}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.status !== '2')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.search)
        filters.push({field:'name', comparator:'contains', value:this.filters.search});
      if (this.filters.excise !== '')
        filters.push({field:'excise', comparator:'eq', value:this.filters.excise});
      return filters;
    }
  },
  async created() {
    const results = await this.$apiCall(`query ProductionBlendingFormulas {
                                              blendingPreferences {
                                                blendDuration
                                              }
                                            }`);
    if (!results.error)
      this.preferences = results.data.blendingPreferences;
    this.loading = false;
  },
  methods: {
    async expandFormula(item, value) {
      if (value && !item.inputs) {
        this.loading = true;
        const results = await this.$apiCall(`query ProductionBlendingFormulas($id:ID!) {
                                                  formula(id:$id) {
                                                    inputs{id,itemName,itemUnits,precision,quantity,type},
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'inputs', results.data.formula.inputs);
        this.loading = false;
      }
    },
    async performDeleteFormula() {
      this.loading = true;
      const results = await this.$apiCall(`mutation ProductionBlendingFormulas($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.formula.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$store.commit('setError', {type:'success',message:`Deleted formula ${this.formula.name}`});
        this.$refs.table?.buildList();
      }
    },
  }
}
</script>